import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi, categoriesApi } from '../services/api';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Building2, Calendar, Users, CreditCard, Settings,
  CheckCircle, XCircle, Clock, Star, TrendingUp, AlertTriangle, Gift, Plus, List, Trash2, KeyRound, UserCog, Copy, X, Mail, Eye, MousePointerClick, Send, MessageSquare, RefreshCw, ChevronDown, ChevronUp, Bell, Download, ExternalLink, DollarSign, Receipt
} from 'lucide-react';
import { AdminAddBusiness, AdminAddEvent, AdminManageListings } from '../components/AdminForms';
import RedeployBanner from '../components/RedeployBanner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState({ businesses: [], events: [] });
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [userFilter, setUserFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [resetLinkModal, setResetLinkModal] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [emailEvents, setEmailEvents] = useState([]);
  const [emailFilter, setEmailFilter] = useState('all');
  const [reviews, setReviews] = useState([]);
  const [reviewStatusFilter, setReviewStatusFilter] = useState('pending_approval');
  const [expiringTrials, setExpiringTrials] = useState([]);
  const [expiringLoading, setExpiringLoading] = useState(false);
  const [expiringWindow, setExpiringWindow] = useState(7);
  const [paymentsSummary, setPaymentsSummary] = useState(null);
  const [paymentProductFilter, setPaymentProductFilter] = useState('all');
  const [redeployStatus, setRedeployStatus] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin) {
      navigate('/admin-login');
      return;
    }
    loadData();
  }, [isAuthenticated, isAdmin, authLoading, navigate]);

  const loadData = async () => {
    try {
      const [statsRes, pendingRes, usersRes, paymentsRes, emailStatsRes, emailEventsRes, reviewsRes, redeployRes, expiringRes, paymentsSummaryRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getPending(),
        adminApi.getUsers(),
        adminApi.getPayments(),
        adminApi.getEmailActivityStats().catch(() => ({ data: null })),
        adminApi.getEmailActivity({ limit: 200 }).catch(() => ({ data: { events: [] } })),
        adminApi.getReviews().catch(() => ({ data: { reviews: [] } })),
        adminApi.getRedeployStatus().catch(() => ({ data: null })),
        adminApi.getExpiringFeatured(7).catch(() => ({ data: { items: [] } })),
        adminApi.getPaymentsSummary().catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setPending(pendingRes.data);
      setUsers(usersRes.data);
      setPayments(paymentsRes.data);
      setEmailStats(emailStatsRes.data);
      setEmailEvents(emailEventsRes.data?.events || []);
      setReviews(reviewsRes.data?.reviews || []);
      setRedeployStatus(redeployRes.data);
      setExpiringTrials(expiringRes.data?.items || []);
      setPaymentsSummary(paymentsSummaryRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshExpiringTrials = async (windowDays) => {
    setExpiringLoading(true);
    try {
      const res = await adminApi.getExpiringFeatured(windowDays);
      setExpiringTrials(res.data?.items || []);
    } catch (error) {
      toast.error('Failed to load expiring trials');
    } finally {
      setExpiringLoading(false);
    }
  };

  const handleSendRenewalEmail = async (businessId, businessName) => {
    if (!window.confirm(`Send renewal CTA email to the owner of "${businessName}"?`)) return;
    setActionLoading(businessId);
    try {
      const res = await adminApi.sendRenewalEmail(businessId);
      toast.success(`Renewal email sent to ${res.data.sent_to}`);
      refreshExpiringTrials(expiringWindow);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send renewal email');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEmailDigest = async () => {
    if (!window.confirm(`Email yourself a digest of all ${expiringTrials.length} expiring trial(s) in the next ${expiringWindow} days?`)) return;
    try {
      const res = await adminApi.emailExpiringDigest(expiringWindow);
      if (res.data.count === 0) {
        toast.info('No expiring trials found in window — nothing to email');
      } else {
        toast.success(`Digest emailed to ${res.data.sent_to} (${res.data.count} listing${res.data.count === 1 ? '' : 's'})`);
      }
    } catch (error) {
      toast.error('Failed to email digest');
    }
  };

  const handleExportPaymentsCsv = () => {
    const filtered = payments.filter(p =>
      (paymentFilter === 'all' || p.payment_status === paymentFilter) &&
      (paymentProductFilter === 'all' || p.product_type === paymentProductFilter)
    );
    if (filtered.length === 0) {
      toast.info('No payments to export');
      return;
    }
    const headers = ['Date', 'User Name', 'User Email', 'Product', 'Listing', 'Amount', 'Status', 'Stripe Session ID', 'Refund ID'];
    const rows = filtered.map(p => [
      new Date(p.created_at).toISOString(),
      (p.user_name || '').replace(/"/g, '""'),
      (p.user_email || '').replace(/"/g, '""'),
      (p.metadata?.product_name || p.product_type || '').replace(/"/g, '""'),
      (p.target_name || '').replace(/"/g, '""'),
      (p.amount || 0).toFixed(2),
      p.payment_status || '',
      p.session_id || '',
      p.refund_id || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} payment${filtered.length === 1 ? '' : 's'} to CSV`);
  };

  const handleReconcilePending = async () => {
    if (!window.confirm('Query Stripe for all pending payments and mark abandoned checkouts as expired? This will clean up any leftover pending entries.')) return;
    try {
      const res = await adminApi.reconcilePendingPayments();
      toast.success(`Reconciled ${res.data.reconciled} pending payment${res.data.reconciled === 1 ? '' : 's'}`);
      loadData();
    } catch (error) {
      toast.error('Reconciliation failed');
    }
  };

  const handleResendReceipt = async (sessionId, userEmail) => {
    if (!window.confirm(`Re-send the payment receipt to ${userEmail || 'the customer'}?`)) return;
    setActionLoading(sessionId);
    try {
      const res = await adminApi.resendReceipt(sessionId);
      toast.success(`Receipt re-sent to ${res.data.sent_to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend receipt');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveBusiness = async (id) => {
    setActionLoading(id);
    try {
      const response = await adminApi.approveBusiness(id);
      toast.success(response.data.free_featured 
        ? 'Business approved with free featured month!' 
        : 'Business approved!');
      loadData();
    } catch (error) {
      toast.error('Failed to approve business');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectBusiness = async (id) => {
    const reason = prompt('Rejection reason (optional):');
    setActionLoading(id);
    try {
      await adminApi.rejectBusiness(id, reason);
      toast.success('Business rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject business');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveEvent = async (id) => {
    setActionLoading(id);
    try {
      await adminApi.approveEvent(id);
      toast.success('Event approved!');
      loadData();
    } catch (error) {
      toast.error('Failed to approve event');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectEvent = async (id) => {
    const reason = prompt('Rejection reason (optional):');
    setActionLoading(id);
    try {
      await adminApi.rejectEvent(id, reason);
      toast.success('Event rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject event');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteBusiness = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    setActionLoading(id);
    try {
      await adminApi.deleteBusiness(id);
      toast.success('Business deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete business');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteEvent = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    setActionLoading(id);
    try {
      await adminApi.deleteEvent(id);
      toast.success('Event deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete event');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePromotion = async () => {
    try {
      await adminApi.togglePromotion(!stats?.promotion_active);
      toast.success(`Promotion ${stats?.promotion_active ? 'disabled' : 'enabled'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to toggle promotion');
    }
  };

  const handleResetFreeFeaturedSlots = async () => {
    const currentUsed = stats?.free_featured_used || 0;
    const currentLimit = stats?.free_featured_limit || 5;
    const confirmMsg = `Reset the Free Featured Promotion counter?\n\nCurrent: ${currentUsed}/${currentLimit} used\nAfter reset: 0/${currentLimit} used\n\nThis allows ${currentLimit} new businesses to claim a free featured month.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await adminApi.updateFreeFeaturedSlots({ reset_count: true, free_featured_limit: currentLimit });
      toast.success(`Slots reset — ${res.data.free_featured_count}/${res.data.free_featured_limit} used`);
      loadData();
    } catch (error) {
      toast.error('Failed to reset free featured slots');
    }
  };

  const handleUpdateFreeFeaturedLimit = async () => {
    const currentLimit = stats?.free_featured_limit || 5;
    const input = window.prompt(`Set a new total limit for free featured slots.\n\nCurrent limit: ${currentLimit}\n\nEnter a new number (e.g., 10) — this does NOT reset the used counter.`, String(currentLimit));
    if (input === null) return;
    const newLimit = parseInt(input, 10);
    if (Number.isNaN(newLimit) || newLimit < 0) {
      toast.error('Please enter a valid non-negative number');
      return;
    }
    try {
      const res = await adminApi.updateFreeFeaturedSlots({ free_featured_limit: newLimit });
      toast.success(`Limit updated — ${res.data.free_featured_count}/${res.data.free_featured_limit} used`);
      loadData();
    } catch (error) {
      toast.error('Failed to update limit');
    }
  };

  const handleToggleUser = async (userId) => {
    setActionLoading(userId);
    try {
      await adminApi.toggleUser(userId);
      toast.success('User status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeUserRole = async (userId, currentRole) => {
    const newRole = currentRole === 'business_owner' ? 'community_member' : 'business_owner';
    const label = newRole === 'business_owner' ? 'Business Owner' : 'Community Member';
    if (!window.confirm(`Change this user to "${label}"?\n\nThey will need to log out and back in to see the change.`)) return;
    setActionLoading(userId);
    try {
      await adminApi.updateUserRole(userId, newRole);
      toast.success(`User changed to ${label}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change user role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateReset = async (user) => {
    if (!window.confirm(`Generate a password reset link for ${user.email}?\n\nThe link will be emailed to them and shown to you.`)) return;
    setActionLoading(user.id);
    try {
      const res = await adminApi.generatePasswordReset(user.id);
      setResetLinkModal({
        email: user.email,
        name: user.name,
        url: res.data.reset_url,
        emailSent: res.data.email_sent,
        emailError: res.data.email_error,
      });
      toast.success(res.data.email_sent ? 'Reset link sent via email' : 'Reset link generated (email failed)');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate reset link');
    } finally {
      setActionLoading(null);
    }
  };

  const copyResetLink = async () => {
    if (!resetLinkModal?.url) return;
    try {
      await navigator.clipboard.writeText(resetLinkModal.url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleSendWelcome = async (user) => {
    if (!window.confirm(`Send the welcome email to ${user.email}?`)) return;
    setActionLoading(user.id);
    try {
      await adminApi.sendWelcomeEmail(user.id);
      toast.success(`Welcome email sent to ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send welcome email');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveReview = async (id) => {
    setActionLoading(id);
    try {
      await adminApi.approveReview(id);
      toast.success('Review approved');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve review');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectReview = async (id) => {
    if (!window.confirm('Reject this review? It will not be shown publicly.')) return;
    setActionLoading(id);
    try {
      await adminApi.rejectReview(id);
      toast.success('Review rejected');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject review');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Permanently delete this review? This cannot be undone.')) return;
    setActionLoading(id);
    try {
      await adminApi.deleteReview(id);
      toast.success('Review deleted');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete review');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-900">
        <div className="spinner border-accent"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'businesses', label: 'Businesses', icon: Building2, badge: pending.businesses.length },
    { id: 'events', label: 'Events', icon: Calendar, badge: pending.events.length },
    { id: 'add-business', label: 'Add Business', icon: Plus },
    { id: 'add-event', label: 'Add Event', icon: Plus },
    { id: 'manage', label: 'Manage Listings', icon: List },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'reviews', label: 'Reviews', icon: MessageSquare, badge: reviews.filter(r => r.status === 'pending_approval').length },
    { id: 'expiring-trials', label: 'Expiring Trials', icon: Bell, badge: expiringTrials.length || undefined },
    { id: 'email-activity', label: 'Email Activity', icon: Mail },
    { id: 'payments', label: 'Payments', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-neutral-900" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen bg-neutral-800 p-4 flex-shrink-0">
          <div className="mb-8">
            <h1 className="text-xl font-bold text-white">Admin Panel</h1>
            <p className="text-neutral-400 text-sm">Explore MHC</p>
          </div>

          <nav className="space-y-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-accent text-neutral-900' 
                    : 'text-neutral-300 hover:bg-neutral-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <div className="flex items-center gap-3">
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </div>
                {tab.badge > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    activeTab === tab.id ? 'bg-neutral-900 text-accent' : 'bg-red-500 text-white'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {/* Redeploy reminder banner */}
          {redeployStatus && (redeployStatus.businesses_pending > 0 || redeployStatus.events_pending > 0) && (
            <RedeployBanner status={redeployStatus} />
          )}

          {activeTab === 'overview' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-white mb-6">Dashboard Overview</h2>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-neutral-800 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <span className="text-neutral-400 text-sm">Pending Submissions</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{stats?.total_pending || 0}</p>
                  {stats?.paid_pending_businesses + stats?.paid_pending_events > 0 && (
                    <p className="text-amber-500 text-sm mt-1">
                      {stats?.paid_pending_businesses + stats?.paid_pending_events} priority (paid)
                    </p>
                  )}
                </div>

                <div className="bg-neutral-800 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Star className="w-5 h-5 text-accent" />
                    <span className="text-neutral-400 text-sm">Active Featured</span>
                  </div>
                  <p className="text-3xl font-bold text-white">
                    {(stats?.active_featured_businesses || 0) + (stats?.active_featured_events || 0)}
                  </p>
                  <p className="text-neutral-500 text-sm mt-1">
                    {stats?.active_featured_businesses} businesses, {stats?.active_featured_events} events
                  </p>
                </div>

                <div className="bg-neutral-800 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    <span className="text-neutral-400 text-sm">Monthly Revenue</span>
                  </div>
                  <p className="text-3xl font-bold text-white">${stats?.monthly_revenue?.toFixed(2) || '0.00'}</p>
                  <p className="text-neutral-500 text-sm mt-1">
                    Total: ${stats?.total_revenue?.toFixed(2) || '0.00'}
                  </p>
                </div>

                <div className="bg-neutral-800 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Gift className="w-5 h-5 text-purple-500" />
                    <span className="text-neutral-400 text-sm">Free Featured Promotion</span>
                  </div>
                  <p className="text-3xl font-bold text-white">
                    {stats?.free_featured_used || 0}/{stats?.free_featured_limit || 5}
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <button
                      onClick={handleTogglePromotion}
                      data-testid="toggle-promotion-btn"
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        stats?.promotion_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {stats?.promotion_active ? 'Active' : 'Disabled'}
                    </button>
                    <button
                      onClick={handleResetFreeFeaturedSlots}
                      data-testid="reset-free-featured-slots-btn"
                      title="Reset the used counter back to 0 (allows the next N businesses to claim a free featured month)"
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors inline-flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Reset Counter
                    </button>
                    <button
                      onClick={handleUpdateFreeFeaturedLimit}
                      data-testid="update-free-featured-limit-btn"
                      title="Change the total number of free featured slots available in this promotion period"
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-neutral-700 text-neutral-200 hover:bg-neutral-600 transition-colors"
                    >
                      Set Limit
                    </button>
                  </div>
                </div>
              </div>

              {/* Revenue by Product */}
              {stats?.revenue_by_product && Object.keys(stats.revenue_by_product).length > 0 && (
                <div className="bg-neutral-800 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Revenue by Product</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(stats.revenue_by_product).map(([product, amount]) => (
                      <div key={product} className="bg-neutral-700 rounded-lg p-4">
                        <p className="text-neutral-400 text-sm capitalize">{product.replace(/_/g, ' ')}</p>
                        <p className="text-xl font-bold text-white">${amount.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'businesses' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-white mb-6">
                Pending Businesses ({pending.businesses.length})
              </h2>

              {pending.businesses.length > 0 ? (
                <div className="space-y-4">
                  {pending.businesses.map(business => (
                    <div key={business.id} className={`bg-neutral-800 rounded-xl p-6 ${business.paid_for_feature ? 'border-2 border-amber-500' : ''}`}>
                      {business.paid_for_feature && (
                        <span className="badge bg-amber-500 text-neutral-900 mb-3">PRIORITY - PAID</span>
                      )}
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-white">{business.business_name}</h3>
                          <p className="text-neutral-400">{business.primary_category}</p>
                          <p className="text-neutral-500 text-sm">{business.street_address}, {business.city_state_zip}</p>
                          <p className="text-neutral-400 text-sm mt-2">{business.description}</p>
                          {business.ownership_tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {business.ownership_tags.map(tag => (
                                <span key={tag} className="badge badge-minority text-xs">{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveBusiness(business.id)}
                            disabled={actionLoading === business.id}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                            data-testid={`approve-business-${business.id}`}
                          >
                            <CheckCircle className="w-4 h-4" /> Approve
                          </button>
                          <button
                            onClick={() => handleRejectBusiness(business.id)}
                            disabled={actionLoading === business.id}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
                          >
                            <XCircle className="w-4 h-4" /> Reject
                          </button>
                          <button
                            onClick={() => handleDeleteBusiness(business.id, business.business_name)}
                            disabled={actionLoading === business.id}
                            className="px-4 py-2 bg-neutral-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                            data-testid={`delete-business-${business.id}`}
                          >
                            <Trash2 className="w-4 h-4" /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-neutral-800 rounded-xl">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-neutral-400">No pending business submissions</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'events' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-white mb-6">
                Pending Events ({pending.events.length})
              </h2>

              {pending.events.length > 0 ? (
                <div className="space-y-4">
                  {pending.events.map(event => (
                    <div key={event.id} className={`bg-neutral-800 rounded-xl p-6 ${event.paid_for_feature ? 'border-2 border-amber-500' : ''}`}>
                      {event.paid_for_feature && (
                        <span className="badge bg-amber-500 text-neutral-900 mb-3">PRIORITY - PAID</span>
                      )}
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-bold text-white">{event.event_name}</h3>
                            <span className="badge badge-category text-xs">{event.submitter_type.replace('_', ' ')}</span>
                          </div>
                          <p className="text-neutral-400">{event.category} • {event.event_date}</p>
                          <p className="text-neutral-500 text-sm">{event.location}</p>
                          <p className="text-neutral-400 text-sm mt-2">{event.description}</p>
                          <p className="text-neutral-500 text-sm mt-2">Submitted by: {event.submitter_name}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveEvent(event.id)}
                            disabled={actionLoading === event.id}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                            data-testid={`approve-event-${event.id}`}
                          >
                            <CheckCircle className="w-4 h-4" /> Approve
                          </button>
                          <button
                            onClick={() => handleRejectEvent(event.id)}
                            disabled={actionLoading === event.id}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
                          >
                            <XCircle className="w-4 h-4" /> Reject
                          </button>
                          <button
                            onClick={() => handleDeleteEvent(event.id, event.event_name)}
                            disabled={actionLoading === event.id}
                            className="px-4 py-2 bg-neutral-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 className="w-4 h-4" /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-neutral-800 rounded-xl">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-neutral-400">No pending event submissions</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="animate-fade-in">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Users ({users.filter(u => u.user_type !== 'admin').length})</h2>
                <div className="flex gap-2">
                  {['all', 'business_owner', 'community_member'].map(filter => (
                    <button
                      key={filter}
                      onClick={() => setUserFilter(filter)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        userFilter === filter 
                          ? 'bg-accent text-neutral-900' 
                          : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
                      }`}
                      data-testid={`user-filter-${filter}`}
                    >
                      {filter === 'all' ? 'All' : filter === 'business_owner' ? 'Business Owners' : 'Community'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-neutral-800 rounded-xl overflow-hidden">
                <table className="admin-table w-full">
                  <thead>
                    <tr>
                      <th className="text-neutral-400">Name</th>
                      <th className="text-neutral-400">Email</th>
                      <th className="text-neutral-400">Type</th>
                      <th className="text-neutral-400">Joined</th>
                      <th className="text-neutral-400">Status</th>
                      <th className="text-neutral-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter(u => u.user_type !== 'admin')
                      .filter(u => userFilter === 'all' || u.user_type === userFilter)
                      .map(user => (
                      <tr key={user.id} className="text-white">
                        <td>{user.name}</td>
                        <td className="text-neutral-400">
                          <div className="flex items-center gap-2">
                            <span>{user.email}</span>
                            {user.email_opened?.welcome && (
                              <span
                                title={`Opened welcome email ${new Date(user.email_opened.welcome).toLocaleString()}`}
                                className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded"
                                data-testid={`badge-opened-welcome-${user.id}`}
                              >
                                <Eye className="w-3 h-3" /> Welcome
                              </span>
                            )}
                            {user.email_clicked?.welcome && (
                              <span
                                title={`Clicked welcome CTA ${new Date(user.email_clicked.welcome).toLocaleString()}`}
                                className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded"
                                data-testid={`badge-clicked-welcome-${user.id}`}
                              >
                                <MousePointerClick className="w-3 h-3" /> Clicked
                              </span>
                            )}
                            {user.email_opened?.verification && (
                              <span
                                title={`Opened verification email ${new Date(user.email_opened.verification).toLocaleString()}`}
                                className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded"
                                data-testid={`badge-opened-verification-${user.id}`}
                              >
                                <Eye className="w-3 h-3" /> Verify
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${user.user_type === 'business_owner' ? 'bg-secondary/20 text-secondary' : 'bg-primary/20 text-primary'}`}>
                            {user.user_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="text-neutral-500 text-sm">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td>
                          <span className={`badge ${user.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {user.is_active !== false ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td>
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              onClick={() => handleChangeUserRole(user.id, user.user_type)}
                              disabled={actionLoading === user.id}
                              className="inline-flex items-center gap-1 text-sm text-secondary hover:text-secondary/80"
                              title={user.user_type === 'business_owner' ? 'Switch to Community Member' : 'Switch to Business Owner'}
                              data-testid={`change-role-${user.id}`}
                            >
                              <UserCog className="w-4 h-4" />
                              {user.user_type === 'business_owner' ? 'Make Community' : 'Make Business'}
                            </button>
                            <button
                              onClick={() => handleGenerateReset(user)}
                              disabled={actionLoading === user.id}
                              className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent/80"
                              title="Generate password reset link"
                              data-testid={`reset-password-${user.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                              Reset Password
                            </button>
                            <button
                              onClick={() => handleSendWelcome(user)}
                              disabled={actionLoading === user.id}
                              className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                              title="Send the welcome / next-steps email"
                              data-testid={`send-welcome-${user.id}`}
                            >
                              <Mail className="w-4 h-4" />
                              Send Welcome
                            </button>
                            <button
                              onClick={() => handleToggleUser(user.id)}
                              disabled={actionLoading === user.id}
                              className="text-sm text-neutral-400 hover:text-white"
                              data-testid={`toggle-user-${user.id}`}
                            >
                              {user.is_active !== false ? 'Disable' : 'Enable'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.filter(u => u.user_type !== 'admin').filter(u => userFilter === 'all' || u.user_type === userFilter).length === 0 && (
                  <div className="text-center py-8 text-neutral-500">No users found</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="animate-fade-in" data-testid="reviews-tab">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">Customer Reviews</h2>
                  <p className="text-sm text-neutral-400">Approve or reject visitor-submitted reviews</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'pending_approval', label: 'Pending Approval' },
                    { id: 'pending_verification', label: 'Awaiting Email Verify' },
                    { id: 'approved', label: 'Approved' },
                    { id: 'rejected', label: 'Rejected' },
                    { id: 'all', label: 'All' },
                  ].map(f => (
                    <button
                      key={f.id}
                      onClick={() => setReviewStatusFilter(f.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${reviewStatusFilter === f.id ? 'bg-accent text-neutral-900' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'}`}
                      data-testid={`reviews-filter-${f.id}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {reviews
                  .filter(r => reviewStatusFilter === 'all' || r.status === reviewStatusFilter)
                  .map(r => (
                    <div key={r.id} className="bg-neutral-800 rounded-xl p-5 border border-neutral-700" data-testid={`review-${r.id}`}>
                      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-semibold text-white">{r.reviewer_name}</span>
                            <span className="text-xs text-neutral-500">&lt;{r.reviewer_email}&gt;</span>
                            <span className={`badge ${
                              r.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                              r.status === 'pending_approval' ? 'bg-amber-500/20 text-amber-400' :
                              r.status === 'pending_verification' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {r.status.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mb-1">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star key={n} className={`w-4 h-4 ${r.rating >= n ? 'fill-amber-400 text-amber-400' : 'text-neutral-600'}`} />
                            ))}
                          </div>
                          <p className="text-sm text-neutral-400 mb-2">
                            For: <span className="text-neutral-200 font-medium">{r.business_name}</span>
                            {r.created_at && <span className="ml-2">· {new Date(r.created_at).toLocaleString()}</span>}
                          </p>
                          {r.comment && <p className="text-neutral-200 whitespace-pre-line">{r.comment}</p>}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {r.status === 'pending_approval' && (
                            <button
                              onClick={() => handleApproveReview(r.id)}
                              disabled={actionLoading === r.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
                              data-testid={`approve-review-${r.id}`}
                            >
                              <CheckCircle className="w-4 h-4" /> Approve
                            </button>
                          )}
                          {(r.status === 'pending_approval' || r.status === 'pending_verification') && (
                            <button
                              onClick={() => handleRejectReview(r.id)}
                              disabled={actionLoading === r.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium"
                              data-testid={`reject-review-${r.id}`}
                            >
                              <XCircle className="w-4 h-4" /> Reject
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteReview(r.id)}
                            disabled={actionLoading === r.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
                            data-testid={`delete-review-${r.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                {reviews.filter(r => reviewStatusFilter === 'all' || r.status === reviewStatusFilter).length === 0 && (
                  <div className="text-center py-12 text-neutral-500 text-sm">No reviews in this category.</div>
                )}
              </div>
            </div>
          )}


          {activeTab === 'expiring-trials' && (
            <div className="animate-fade-in" data-testid="expiring-trials-tab">
              <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <div>
                  <h2 className="text-2xl font-bold text-white">Expiring Free Trials</h2>
                  <p className="text-sm text-neutral-400 mt-1">
                    Businesses whose free featured month is ending soon or has recently expired. Send a one-click renewal CTA, or email yourself the whole digest.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-xs text-neutral-400 font-semibold uppercase tracking-wider">Window:</label>
                  <select
                    value={expiringWindow}
                    onChange={(e) => {
                      const w = parseInt(e.target.value, 10);
                      setExpiringWindow(w);
                      refreshExpiringTrials(w);
                    }}
                    className="bg-neutral-800 text-white text-sm rounded-lg px-3 py-2 border border-neutral-700"
                    data-testid="expiring-window-select"
                  >
                    <option value={3}>±3 days</option>
                    <option value={7}>±7 days</option>
                    <option value={14}>±14 days</option>
                    <option value={30}>±30 days</option>
                  </select>
                  <button
                    onClick={() => refreshExpiringTrials(expiringWindow)}
                    disabled={expiringLoading}
                    className="px-3 py-2 bg-neutral-800 text-white text-sm rounded-lg border border-neutral-700 hover:bg-neutral-700 inline-flex items-center gap-2"
                    data-testid="refresh-expiring-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${expiringLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                  <button
                    onClick={handleEmailDigest}
                    disabled={expiringTrials.length === 0}
                    className="px-4 py-2 bg-accent text-white text-sm font-semibold rounded-lg hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    data-testid="email-digest-btn"
                  >
                    <Send className="w-4 h-4" />
                    Email me this digest
                  </button>
                </div>
              </div>

              {expiringTrials.length === 0 ? (
                <div className="bg-neutral-800 rounded-xl p-10 text-center">
                  <Bell className="w-12 h-12 text-neutral-600 mx-auto mb-3" />
                  <p className="text-neutral-300 font-semibold">No trials expiring in this window</p>
                  <p className="text-neutral-500 text-sm mt-1">Adjust the window or check back later.</p>
                </div>
              ) : (
                <div className="bg-neutral-800 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-neutral-900 text-left text-xs uppercase tracking-wider text-neutral-400">
                        <th className="px-4 py-3">Business</th>
                        <th className="px-4 py-3">Owner</th>
                        <th className="px-4 py-3">Ends</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expiringTrials.map((trial) => {
                        const days = trial.days_remaining;
                        const expired = days < 0;
                        const today = days >= 0 && days < 1;
                        const statusBadge = expired
                          ? `bg-red-500/20 text-red-400`
                          : today
                            ? `bg-amber-500/20 text-amber-400`
                            : `bg-blue-500/20 text-blue-400`;
                        const statusLabel = expired
                          ? `Expired ${Math.abs(Math.round(days))}d ago`
                          : today
                            ? 'Ends today'
                            : `${Math.round(days)}d left`;
                        const reminderSent = trial.renewal_reminder_sent_at;
                        return (
                          <tr key={trial.id} className="border-t border-neutral-700 hover:bg-neutral-700/30">
                            <td className="px-4 py-3">
                              <div className="font-semibold text-white">{trial.business_name}</div>
                              <div className="text-xs text-neutral-400">{trial.primary_category || '—'}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-300">
                              <div>{trial.owner_name || '—'}</div>
                              <div className="text-xs text-neutral-500">{trial.owner_email || 'no email'}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-300">
                              {trial.featured_until ? new Date(trial.featured_until).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${statusBadge}`}>{statusLabel}</span>
                              {reminderSent && (
                                <div className="text-xs text-neutral-500 mt-1">
                                  Sent {new Date(reminderSent).toLocaleDateString()}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => handleSendRenewalEmail(trial.id, trial.business_name)}
                                disabled={actionLoading === trial.id || !trial.owner_email}
                                className="px-3 py-1.5 bg-accent text-white text-xs font-semibold rounded-lg hover:bg-accent/90 disabled:opacity-40 inline-flex items-center gap-1.5"
                                data-testid={`send-renewal-${trial.id}`}
                              >
                                <Mail className="w-3.5 h-3.5" />
                                {actionLoading === trial.id ? 'Sending...' : (reminderSent ? 'Resend' : 'Send renewal email')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}


          {activeTab === 'email-activity' && (
            <div className="animate-fade-in" data-testid="email-activity-tab">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">Email Activity</h2>
                  <p className="text-sm text-neutral-400">
                    Live tracking from Resend webhook
                    {emailStats && (
                      <span className={`ml-2 inline-block px-2 py-0.5 text-xs rounded ${emailStats.webhook_configured ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {emailStats.webhook_configured ? 'Webhook configured' : 'Webhook signature unverified'}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {emailStats?.totals && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: 'Sent', val: emailStats.totals.sent || 0, color: 'bg-blue-500/20 text-blue-400', Icon: Send },
                    { label: 'Opened', val: emailStats.totals.opened || 0, color: 'bg-green-500/20 text-green-400', Icon: Eye },
                    { label: 'Clicked', val: emailStats.totals.clicked || 0, color: 'bg-purple-500/20 text-purple-400', Icon: MousePointerClick },
                    { label: 'Bounced', val: emailStats.totals.bounced || 0, color: 'bg-red-500/20 text-red-400', Icon: AlertTriangle },
                  ].map(({ label, val, color, Icon }) => (
                    <div key={label} className="bg-neutral-800 rounded-xl p-4 border border-neutral-700">
                      <div className={`inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-semibold ${color} mb-2`}>
                        <Icon className="w-3 h-3" />
                        {label}
                      </div>
                      <div className="text-3xl font-bold text-white">{val}</div>
                    </div>
                  ))}
                </div>
              )}

              {emailStats?.by_type && Object.keys(emailStats.by_type).length > 0 && (
                <div className="bg-neutral-800 rounded-xl border border-neutral-700 mb-8 overflow-hidden">
                  <div className="px-5 py-3 border-b border-neutral-700">
                    <h3 className="text-white font-semibold">By Email Type</h3>
                  </div>
                  <table className="admin-table w-full">
                    <thead>
                      <tr>
                        <th className="text-neutral-400">Type</th>
                        <th className="text-neutral-400">Sent</th>
                        <th className="text-neutral-400">Opened</th>
                        <th className="text-neutral-400">Clicked</th>
                        <th className="text-neutral-400">Open rate</th>
                        <th className="text-neutral-400">Click rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(emailStats.by_type).map(([type, c]) => {
                        const sent = c.sent || 0;
                        const openRate = sent ? Math.round(((c.opened || 0) / sent) * 100) : 0;
                        const clickRate = sent ? Math.round(((c.clicked || 0) / sent) * 100) : 0;
                        return (
                          <tr key={type} className="text-white">
                            <td><span className="badge bg-neutral-700 text-neutral-200">{type}</span></td>
                            <td>{sent}</td>
                            <td>{c.opened || 0}</td>
                            <td>{c.clicked || 0}</td>
                            <td className="text-green-400">{openRate}%</td>
                            <td className="text-purple-400">{clickRate}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="bg-neutral-800 rounded-xl border border-neutral-700 overflow-hidden">
                <div className="px-5 py-3 border-b border-neutral-700 flex items-center justify-between flex-wrap gap-3">
                  <h3 className="text-white font-semibold">Recent events ({emailEvents.length})</h3>
                  <div className="flex gap-2 flex-wrap">
                    {['all', 'email.sent', 'email.opened', 'email.clicked', 'email.bounced'].map(f => (
                      <button
                        key={f}
                        onClick={() => setEmailFilter(f)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${emailFilter === f ? 'bg-accent text-neutral-900' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'}`}
                        data-testid={`email-filter-${f}`}
                      >
                        {f === 'all' ? 'All' : f.replace('email.', '')}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="admin-table w-full">
                    <thead>
                      <tr>
                        <th className="text-neutral-400">When</th>
                        <th className="text-neutral-400">Event</th>
                        <th className="text-neutral-400">Type</th>
                        <th className="text-neutral-400">Recipient</th>
                        <th className="text-neutral-400">Subject / Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailEvents
                        .filter(ev => emailFilter === 'all' || ev.type === emailFilter)
                        .slice(0, 100)
                        .map(ev => (
                          <tr key={ev.id} className="text-white">
                            <td className="text-neutral-400 text-xs whitespace-nowrap">
                              {ev.created_at ? new Date(ev.created_at).toLocaleString() : '—'}
                            </td>
                            <td>
                              <span className={`badge ${
                                ev.type === 'email.opened' ? 'bg-green-500/20 text-green-400' :
                                ev.type === 'email.clicked' ? 'bg-purple-500/20 text-purple-400' :
                                ev.type === 'email.bounced' ? 'bg-red-500/20 text-red-400' :
                                ev.type === 'email.sent' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-neutral-700 text-neutral-300'
                              }`}>
                                {(ev.type || '').replace('email.', '') || '—'}
                              </span>
                            </td>
                            <td className="text-neutral-300 text-sm">{ev.email_type || '—'}</td>
                            <td className="text-neutral-300 text-sm">{ev.to || '—'}</td>
                            <td className="text-neutral-400 text-sm truncate max-w-xs">
                              {ev.click_url ? (
                                <a href={ev.click_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                                  {ev.click_url}
                                </a>
                              ) : (
                                ev.subject || '—'
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {emailEvents.filter(ev => emailFilter === 'all' || ev.type === emailFilter).length === 0 && (
                    <div className="text-center py-8 text-neutral-500 text-sm">No events yet. They appear here as Resend delivers, opens, and clicks come in.</div>
                  )}
                </div>
              </div>
            </div>
          )}


          {activeTab === 'payments' && (
            <div className="animate-fade-in" data-testid="payments-tab">
              <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <div>
                  <h2 className="text-2xl font-bold text-white">Payment History</h2>
                  <p className="text-sm text-neutral-400 mt-1">
                    Stripe transactions across business and event featured placements
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleReconcilePending}
                    className="px-4 py-2 bg-neutral-700 text-white text-sm font-semibold rounded-lg hover:bg-neutral-600 inline-flex items-center gap-2"
                    data-testid="reconcile-pending-btn"
                    title="Query Stripe for pending payments and mark abandoned checkouts as expired"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reconcile Pending
                  </button>
                  <button
                    onClick={handleExportPaymentsCsv}
                    className="px-4 py-2 bg-neutral-700 text-white text-sm font-semibold rounded-lg hover:bg-neutral-600 inline-flex items-center gap-2"
                    data-testid="export-payments-csv-btn"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                </div>
              </div>

              {/* Summary Cards */}
              {paymentsSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="payments-summary-cards">
                  <div className="bg-neutral-800 rounded-xl p-5 border border-neutral-700">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      <span className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">Net Revenue</span>
                    </div>
                    <p className="text-2xl font-bold text-white">${paymentsSummary.net_revenue.toFixed(2)}</p>
                    <p className="text-xs text-neutral-500 mt-1">Gross ${paymentsSummary.total_revenue.toFixed(2)} − Refunds ${paymentsSummary.total_refunded.toFixed(2)}</p>
                  </div>
                  <div className="bg-neutral-800 rounded-xl p-5 border border-neutral-700">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-blue-400" />
                      <span className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">This Month</span>
                    </div>
                    <p className="text-2xl font-bold text-white">${paymentsSummary.month_revenue.toFixed(2)}</p>
                    <p className="text-xs text-neutral-500 mt-1">{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                  </div>
                  <div className="bg-neutral-800 rounded-xl p-5 border border-neutral-700">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">Paid</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{paymentsSummary.paid_count}</p>
                    <p className="text-xs text-neutral-500 mt-1">{paymentsSummary.pending_count} pending</p>
                  </div>
                  <div className="bg-neutral-800 rounded-xl p-5 border border-neutral-700">
                    <div className="flex items-center gap-2 mb-1">
                      <RefreshCw className="w-4 h-4 text-amber-400" />
                      <span className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">Refunds</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{paymentsSummary.refunded_count}</p>
                    {paymentsSummary.failed_refund_count > 0 ? (
                      <p className="text-xs text-red-400 mt-1">{paymentsSummary.failed_refund_count} failed — needs attention</p>
                    ) : (
                      <p className="text-xs text-neutral-500 mt-1">All refunds processed</p>
                    )}
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs text-neutral-500 uppercase font-semibold tracking-wider mr-1">Status:</span>
                {['all', 'paid', 'pending', 'refunded', 'refund_failed'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setPaymentFilter(filter)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      paymentFilter === filter
                        ? 'bg-accent text-neutral-900'
                        : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
                    }`}
                    data-testid={`payment-filter-${filter}`}
                  >
                    {filter === 'refund_failed' ? 'Refund Failed' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
                <span className="text-xs text-neutral-500 uppercase font-semibold tracking-wider mx-3">Type:</span>
                {['all', 'business_monthly', 'business_weekly', 'event_weekly', 'event_until_date'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setPaymentProductFilter(filter)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      paymentProductFilter === filter
                        ? 'bg-accent text-neutral-900'
                        : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
                    }`}
                    data-testid={`payment-product-filter-${filter}`}
                  >
                    {filter === 'all' ? 'All' : filter.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                ))}
              </div>

              {/* Transactions Table */}
              <div className="bg-neutral-800 rounded-xl overflow-hidden">
                <table className="admin-table w-full">
                  <thead>
                    <tr>
                      <th className="text-neutral-400">Date</th>
                      <th className="text-neutral-400">User</th>
                      <th className="text-neutral-400">Listing</th>
                      <th className="text-neutral-400">Product</th>
                      <th className="text-neutral-400 text-right">Amount</th>
                      <th className="text-neutral-400">Status</th>
                      <th className="text-neutral-400">Stripe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments
                      .filter(p =>
                        (paymentFilter === 'all' || p.payment_status === paymentFilter) &&
                        (paymentProductFilter === 'all' || p.product_type === paymentProductFilter)
                      )
                      .map(payment => {
                        const isBiz = payment.target_type === 'business';
                        const listingUrl = payment.target_slug
                          ? (isBiz ? `/businesses/${payment.target_slug}` : `/events/${payment.target_slug}`)
                          : null;
                        const stripeUrl = payment.session_id
                          ? `https://dashboard.stripe.com/${payment.session_id.startsWith('cs_test_') ? 'test/' : ''}payments?session_id=${payment.session_id}`
                          : null;
                        return (
                          <tr key={payment.id} className="text-white" data-testid={`payment-row-${payment.id}`}>
                            <td className="text-neutral-400 text-sm">
                              {new Date(payment.created_at).toLocaleDateString()}
                              <div className="text-xs text-neutral-600">{new Date(payment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                            </td>
                            <td>
                              <div className="text-sm text-white">{payment.user_name || 'Unknown'}</div>
                              <div className="text-xs text-neutral-500">{payment.user_email || ''}</div>
                            </td>
                            <td className="text-sm text-neutral-200">
                              {listingUrl ? (
                                <a href={listingUrl} target="_blank" rel="noopener noreferrer" className="hover:text-accent inline-flex items-center gap-1">
                                  {payment.target_name}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : (
                                <span>{payment.target_name || '—'}</span>
                              )}
                              <div className="text-xs text-neutral-500">{isBiz ? 'Business' : payment.target_type === 'event' ? 'Event' : '—'}</div>
                            </td>
                            <td className="text-sm">{payment.metadata?.product_name || payment.product_type}</td>
                            <td className="text-right font-semibold">${(payment.amount || 0).toFixed(2)}</td>
                            <td>
                              <span className={`badge ${
                                payment.payment_status === 'paid'
                                  ? 'bg-green-500/20 text-green-400'
                                  : payment.payment_status === 'refunded'
                                    ? 'bg-amber-500/20 text-amber-400'
                                    : payment.payment_status === 'refund_failed'
                                      ? 'bg-red-500/20 text-red-400'
                                      : 'bg-neutral-500/20 text-neutral-400'
                              }`}>
                                {payment.payment_status === 'refund_failed' ? 'refund failed' : payment.payment_status}
                              </span>
                              {payment.refund_error && (
                                <div className="text-xs text-red-400 mt-1 max-w-[200px] truncate" title={payment.refund_error}>
                                  {payment.refund_error}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                {stripeUrl ? (
                                  <a
                                    href={stripeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-neutral-400 hover:text-accent inline-flex items-center gap-1 font-mono"
                                    title={payment.session_id}
                                  >
                                    {payment.session_id.slice(-8)}
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                ) : (
                                  <span className="text-xs text-neutral-600">—</span>
                                )}
                                {payment.payment_status === 'paid' && payment.session_id && (
                                  <button
                                    onClick={() => handleResendReceipt(payment.session_id, payment.user_email)}
                                    disabled={actionLoading === payment.session_id}
                                    className="text-xs text-neutral-500 hover:text-accent inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-neutral-700/50"
                                    title={payment.receipt_sent_at ? `Receipt last sent ${new Date(payment.receipt_sent_at).toLocaleString()} — click to resend` : 'Send receipt email to customer'}
                                    data-testid={`resend-receipt-${payment.id}`}
                                  >
                                    <Mail className="w-3 h-3" />
                                    {actionLoading === payment.session_id ? '…' : (payment.receipt_sent_at ? 'Resend' : 'Receipt')}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {payments.filter(p =>
                  (paymentFilter === 'all' || p.payment_status === paymentFilter) &&
                  (paymentProductFilter === 'all' || p.product_type === paymentProductFilter)
                ).length === 0 && (
                  <div className="text-center py-10 text-neutral-500">
                    <Receipt className="w-12 h-12 mx-auto mb-2 opacity-40" />
                    <p>No payments match these filters</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add Business Tab */}
          {activeTab === 'add-business' && (
            <AdminAddBusiness onSuccess={loadData} />
          )}

          {/* Add Event Tab */}
          {activeTab === 'add-event' && (
            <AdminAddEvent onSuccess={loadData} />
          )}

          {/* Manage Listings Tab */}
          {activeTab === 'manage' && (
            <AdminManageListings onRefresh={loadData} />
          )}
        </main>
      </div>

      {resetLinkModal && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          data-testid="reset-link-modal"
        >
          <div className="bg-neutral-800 rounded-xl max-w-lg w-full p-6 border border-neutral-700">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-accent" />
                  Password Reset Link
                </h3>
                <p className="text-sm text-neutral-400 mt-1">
                  For {resetLinkModal.name} ({resetLinkModal.email})
                </p>
              </div>
              <button
                onClick={() => setResetLinkModal(null)}
                className="text-neutral-400 hover:text-white"
                data-testid="close-reset-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className={`text-sm mb-4 px-3 py-2 rounded-lg ${
              resetLinkModal.emailSent
                ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              {resetLinkModal.emailSent
                ? 'Email sent successfully to the user.'
                : `Email could not be sent${resetLinkModal.emailError ? `: ${resetLinkModal.emailError}` : '.'} Share the link below manually.`}
            </div>

            <label className="block text-xs text-neutral-400 mb-1">Reset link (valid 24 hours)</label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={resetLinkModal.url}
                className="flex-1 px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white text-sm font-mono"
                data-testid="reset-link-input"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={copyResetLink}
                className="inline-flex items-center gap-1 px-4 py-2 bg-accent text-neutral-900 rounded-lg font-semibold hover:bg-accent/90"
                data-testid="copy-reset-link"
              >
                <Copy className="w-4 h-4" />
                Copy
              </button>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setResetLinkModal(null)}
                className="px-4 py-2 bg-neutral-700 text-white rounded-lg hover:bg-neutral-600"
                data-testid="done-reset-modal"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
